<template>
  <div
    class="rounded border border-solid border-gray-500 p-2"
    :data-testid="`rating-segment-${i}`"
  >
    <div class="mb-2 font-inter text-xs font-medium text-gray-50">
      #{{ i + 1 }}
    </div>
    <div v-if="segmentMode === 'image'" class="space-y-2">
      <ImageButtons
        :model-value="getSegmentAttr(i, 'src')"
        show-replace
        :index="i"
        :data-testid="`rating-segment-image-buttons-${i}`"
        @update:model-value="(val) => updateSegmentAttr(i, 'src', val || '')"
      />
      <t-input
        :model-value="getSegmentAttr(i, 'src')"
        :label="$t('forms.rating_segment_image_url')"
        placeholder="https://"
        :data-testid="`rating-segment-src-${i}`"
        @update:model-value="(val) => updateSegmentAttr(i, 'src', val)"
        @on-blur="onSrcBlur(i)"
      />
      <t-input
        :model-value="getSegmentAttr(i, 'alt')"
        :label="$t('forms.rating_segment_alt')"
        :data-testid="`rating-segment-alt-${i}`"
        @update:model-value="(val) => updateSegmentAttr(i, 'alt', val)"
      />
    </div>
    <t-input
      v-if="segmentMode === 'text' || showImageLabel"
      :model-value="getSegmentAttr(i, 'label')"
      :label="$t('forms.rating_segment_label')"
      :data-testid="`rating-segment-label-${i}`"
      @update:model-value="(val) => updateSegmentAttr(i, 'label', val)"
    />
    <t-input
      v-if="urlMode === 'pattern'"
      :model-value="String(getSegmentAttr(i, 'value') ?? '')"
      :label="$t('forms.rating_segment_value')"
      :data-testid="`rating-segment-value-${i}`"
      class="mt-2"
      @update:model-value="(val) => updateSegmentAttr(i, 'value', val)"
    />
    <t-input
      v-else
      :model-value="getSegmentAttr(i, 'href')"
      :label="$t('forms.link')"
      placeholder="https://"
      :data-testid="`rating-segment-href-${i}`"
      class="mt-2"
      @update:model-value="(val) => updateSegmentAttr(i, 'href', val)"
      @on-blur="onHrefBlur(i)"
    />
  </div>
</template>

<script setup lang="ts">
  import ImageButtons from "../components/ImageButtons/ImageButtons.vue";
  import { useMjmlStore } from "../../stores/mjmlStore";
  import { TopolRatingBlock } from "@/schemas/template/blocks/ratingBlockSchema";
  import validHref from "@/utils/validHref";

  import { TInput } from "@topol/twig-ui";

  import { computed } from "vue";

  defineProps<{
    i: number;
    segmentMode: "text" | "image";
    urlMode: string;
  }>();

  const mjmlStore = useMjmlStore();

  const activeBlock = computed(
    () => mjmlStore.getActiveBlock as TopolRatingBlock | undefined
  );

  // In image mode the label is hidden in the rendered email until the
  // segment-label toggle is on, so don't surface the input either — setting
  // a value the user can't see is confusing.
  const showImageLabel = computed(
    () => (activeBlock.value as any)?.imageLabelAttributes?.show === true
  );

  function updateSegmentAttr(childIndex: number, prop: string, value: any) {
    mjmlStore.updateBlockChildrenAttribute({ childIndex, prop, value });
  }

  function onHrefBlur(childIndex: number) {
    const href = String(getSegmentAttr(childIndex, "href") ?? "");
    if (href && !validHref(href)) {
      updateSegmentAttr(childIndex, "href", "https://" + href);
    }
  }

  function onSrcBlur(childIndex: number) {
    const src = String(getSegmentAttr(childIndex, "src") ?? "");
    if (src && !validHref(src)) {
      updateSegmentAttr(childIndex, "src", "https://" + src);
    }
  }

  // Reads via the store getter so translated child attrs (mj-rating
  // segment label/alt for current language) are reflected in the form.
  function getSegmentAttr(childIndex: number, prop: string) {
    const fromStore = mjmlStore.getBlockChildrenAttribute(childIndex, prop);
    if (fromStore !== undefined && fromStore !== "") return fromStore;
    const block = activeBlock.value as any;
    return block?.children?.[childIndex]?.attributes?.[prop] ?? "";
  }
</script>
